import React from "react";
import classNames from "classnames";
import { importedSettlementKey } from "@common/helpers/importedSettlement";
import { ImportedSettlementDisplay } from "@common/schemas/domains/accrualReview/imported-settlement-display.schema";
import shellStyles from "@components/transactions/MatchedTransactionCard/MatchCardShell.module.scss";
import { Amount } from "@design-system/Amount/Amount";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { formatDateOnly } from "@helpers/date";
import styles from "./ImportedSettlementsCard.module.scss";

const DATED_TOOLTIP =
    "Recorded in QuickBooks before your bookkeeping start date. It's already in your books through the imported ledger.";
const UNDATED_TOOLTIP =
    "QuickBooks doesn't record when or how this was settled.";

interface Props {
    documentType: "bill" | "invoice";
    settlements: ImportedSettlementDisplay[];
}

export function getSettlementTooltip(
    settlement: ImportedSettlementDisplay,
): string {
    return settlement.paymentDateOnly === null
        ? UNDATED_TOOLTIP
        : DATED_TOOLTIP;
}

export function getSettlementLabel({
    settlement,
    documentType,
}: {
    settlement: ImportedSettlementDisplay;
    documentType: Props["documentType"];
}): string {
    if (settlement.paymentDateOnly === null) {
        return `Before ${formatDateOnly(settlement.cutoverDate)} · Settled in QuickBooks`;
    }
    const paymentLabel =
        documentType === "bill"
            ? "QuickBooks bill payment"
            : "QuickBooks payment";
    return `${formatDateOnly(settlement.paymentDateOnly)} · ${paymentLabel}`;
}

export const ImportedSettlementsCard: React.FC<Props> = ({
    documentType,
    settlements,
}) => {
    if (settlements.length === 0) {
        return null;
    }

    return (
        <div className={shellStyles.shell}>
            <HStack
                justify="between"
                align="center"
                className={classNames(
                    "mb-0",
                    shellStyles.control,
                    styles.header,
                )}
            >
                <span className={shellStyles.insideLabel}>
                    Settled in QuickBooks
                </span>
            </HStack>
            <VStack className={styles.rows}>
                {settlements.map((settlement) => (
                    <Tooltip
                        key={importedSettlementKey(settlement)}
                        content={getSettlementTooltip(settlement)}
                    >
                        <HStack
                            justify="between"
                            align="center"
                            gap={8}
                            paddingBlock={6}
                            paddingInline={20}
                            className={styles.row}
                        >
                            <span className={styles.rowLabel}>
                                {getSettlementLabel({
                                    settlement,
                                    documentType,
                                })}
                            </span>
                            <span className={styles.rowAmount}>
                                <Amount
                                    amount={settlement.amount}
                                    separateCurrencySign
                                />
                            </span>
                        </HStack>
                    </Tooltip>
                ))}
            </VStack>
        </div>
    );
};
